import { Component, OnInit, ViewChild } from '@angular/core';
import { ImportsModule } from '../../imports';
import { ChartService } from '../../services/chart.service';
import { ChartData } from '../ChartData';
import { UIChart } from 'primeng/chart';
import { Column } from '../Column';
import { MessageService, TreeNode } from 'primeng/api';
import { DetailTableData } from '../../shared/types';
import { DetailTableComponent } from "../../shared/components/detail-table/detail-table.component";

@Component({
  selector: 'app-source',
  standalone: true,
  imports: [
    ImportsModule,
    DetailTableComponent
  ],
  templateUrl: './source.component.html',
  styleUrl: './source.component.css'
})
export class SourceComponent implements OnInit {
  @ViewChild('chart') chart!: UIChart;
  chartResults: ChartData = {
    labels: [],
    datasets: []
  }
  chartOptions: any;
  phaseValue: any[] = [];
  tableColumns: Column[] = [];
  detailColumns: Column[] = [];
  treeTableResults: TreeNode[] = [];
  detailTableResults: any[] = [];
  filteredDetailTableResults: any[] = [];
  globalFilterFields: string[] = [];
  selectedValues: { [key: string]: any[] } = {};
  selectedMatchModes: { [key: string]: string } = {};
  showDetail = false;
  showLoader = false;
  sortedColumns: any[] = [];
  detailTableData: DetailTableData = {} as DetailTableData;

  constructor(private chartService: ChartService, private messageService: MessageService) { }
  ngOnInit() {
    const scalesOptions = {
      x: {
        ticks: {
          display: false
        },
        grid: {
          display: false
        }
      }
    }
    this.chartOptions = {
      scales: scalesOptions,
      indexAxis: 'y',
      maxBarThickness: 20,
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: true,
          align: 'center',
          color: '#186182',
          font: {
            size: 16
          },
          text: "Analysis Stats"
        },
        datalabels: {
          display: true,
          color: 'black',
          align: 'end',
          anchor: 'end',
          offset: -1,
          formatter: (value: any) => {
            return value + '%'
          }
        }
      }
    }
    this.chartService.getSourceSummary(this.phaseValue).subscribe({
      next: (data: any) => {
        if(data.rows.length === 0) return;
        data.rows.sort((a: any, b: any) => b.pctoftotal - a.pctoftotal);
        const dataChart = data.rows.map((item: any) => {
          this.chartResults.labels.push(item.object_srcmatch == null ? 'None' : item.object_srcmatch );
          return item.pctoftotal;
        });
        this.chartResults.datasets.push({
          label: '',
          data: dataChart,
          backgroundColor: '#0E9ED5',
          borderColor: '#0E9ED5'
        });

      },
      complete: () => {
        this.chart.reinit();
      }
    });

    this.tableColumns = [
      { label: 'Attribute', field: 'attribute', width: 135 },  
      { label: 'Source Analysis Results', field: 'srcmatch', width: 220 },
      { label: 'Source Library', field: 'srcLibrary', width: 175 },
      { label: 'Source File', field: 'srcFile', width: 175 },
      { label: 'Member Name', field: 'memberName', width: 175 },
      { label: 'Lines of Code', field: 'loc', width: 175, align: 'right' },
      { label: 'Total Objects', field: 'totalobjects', width: 175, align: 'right' }
    ]

    this.showLoader = true;
    this.chartService.getSourceAnalysisOverview().subscribe({
      next: (data: any) => {
        this.showLoader = false;
        if(data.rows.length === 0) return;
        this.treeTableResults = data.rows.map((item: any) => {
          return {
            data: {
              attribute: item.object_attribute,
              srcmatch: '',
              srcLibrary: '',
              srcFile: '',
              memberName: '',
              loc: item.loc == null ? 0 : item.loc,
              totalobjects: item.totalobjects == null ? 0 : item.totalobjects
            }
          }
        });
      },
      complete: () => {
        this.treeTableResults.forEach((item: any) => {
          this.chartService.getSourceAnalysisOverviewMedium(item.data.attribute).subscribe({
            next: (data: any) => {
              if(data.rows.length === 0) return;
              item.children = data.rows.map((child: any) => {
                return{
                  data: {
                    attribute: item.data.attribute,
                    srcmatch: child.object_srcmatch,
                    srcLibrary: '',
                    scrFile: '',
                    memberName: '',
                    loc: child.loc == null ? 0 : child.loc,
                    totalobjects: child.totalobjects == null ? 0 : child.totalobjects
                  },
                  leaf: true
                }
              });
            }
          })
        });
      }
    });

    this.detailColumns = [
      { label: 'Scope Phase', field: 'scopedesc', width: 100, filterType: 'select' },
      { label: 'Resolved Scope', field: 'resolved_scope', width: 200, filterType: 'select' },
      { label: 'Source Library', field: 'source_library', width: 175, filterType: 'text', matchMode: 'contains' },
      { label: 'Source File', field: 'source_file', width: 175, filterType: 'text', matchMode: 'contains' },
      { label: 'Source Member', field: 'source_member', width: 175, filterType: 'text', matchMode: 'contains' },
      { label: 'Source Text', field: 'source_text', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Source Last Change Date', field: 'source_lastchgdate', width: 175, filterType: 'date' },
      { label: 'Library', field: 'object_library', width: 175, filterType: 'text', matchMode: 'contains' },
      { label: 'Name', field: 'object_name', width: 175, filterType: 'text', matchMode: 'contains' },
      { label: 'Type', field: 'object_type', width: 100, filterType: 'select' },
      { label: 'Attribute', field: 'object_attribute', width: 150, filterType: 'select' },
      { label: 'Text', field: 'object_text', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Source Lines', field: 'object_srclines', width: 150, filterType: 'text', matchMode: "contains" },
      { label: 'Source Match', field: 'object_srcmatch', width: 150, filterType: 'text', matchMode: "contains" },
      { label: 'Reason', field: 'object_reason', width: 175, filterType: 'text', matchMode: "contains" },
      { label: 'Notes', field: 'object_notes', width: 175, filterType: 'text', matchMode: 'contains' },
      { label: 'App Area ID', field: 'apparea_areaid', width: 125, filterType: 'select' },
      { label: 'App Area Description', field: 'apparea_description', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Created', field: 'object_created', width: 175, filterType: 'date' },
      { label: 'Changed', field: 'object_changed', width: 175, filterType: 'date' },
      { label: 'Last Used', field: 'object_lastused', width: 175, filterType: 'date' },
      { label: 'Restored', field: 'object_restored', width: 175, filterType: 'date' }
    ];
  }

  showDetailedTable(col: any, rowNode: any) {
    const { srcmatch, attribute } = rowNode.node.data;
    if(col === 'srcmatch' && rowNode.node.leaf === true){
      this.showDetail = true;
      this.chartService.getSourceAnalysisOverviewDetail(attribute, srcmatch).subscribe({
        next: (data: any) => {
          if(data.rows.length === 0) return;
          this.detailTableResults = data.rows.map((item: any) => {
            return {
              scopedesc: item.scopedesc == null ? 'None' : item.scopedesc,
              resolved_scope: item.resolved_scope,
              source_library: item.source_library,
              source_file: item.source_file,
              source_member: item.source_member,
              source_text: item.source_text,
              source_lastchgdate: item.source_lastchgdate,
              object_library: item.object_library,
              object_name: item.object_name,
              object_type: item.object_type,
              object_attribute: item.object_attribute,
              object_text: item.object_text,
              object_created: item.object_created,
              object_changed: item.object_changed,
              object_lastused: item.object_lastused,
              object_restored: item.object_restored,
              object_srclines: item.object_srclines,
              object_srcmatch: item.object_srcmatch,
              object_reason: item.object_reason,
              object_notes: item.object_notes,
              apparea_areaid: item.apparea_areaid,
              apparea_description: item.apparea_description
            }
          });
          this.filteredDetailTableResults = this.detailTableResults;
          this.detailTableData = {
            items: this.detailTableResults,
            columns: this.detailColumns,
            globalFilterFields: this.globalFilterFields,
            detailTableResults: this.detailTableResults
          }
        }
      })
    }
  }

  isNumber(value: any): boolean {
    return !isNaN(parseFloat(value)) && isFinite(value);
  }

  backToSummary() {
    this.showDetail = false;
    this.filteredDetailTableResults = [];
  }

  async exportChart() {
    const base64Image = this.chart.chart.toBase64Image();
    const response = await fetch(base64Image);
    const blob = await response.blob();
    const clipboardItem = new ClipboardItem({ 'image/png': blob });
    navigator.clipboard.write([clipboardItem]);
    this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Chart image is now in your clipboard', life: 3000, key:'bc' });
  }
}
